import { CRISP_WEBSITE_ID } from "@-/dev.improve.haniverse.lib.crisp";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { openBrowserAsync } from "@-/com.npmjs.expo-web-browser";
import {
  SettingsRow,
  SettingsRowGroup,
} from "@-/dev.improve.haniverse.routes.nav.app.settings._components.settings-row";
import { View } from "@-/com.npmjs.react-native";
import { useState } from "react";
import { AboutSettingsSection } from "@-/dev.improve.haniverse.routes.nav.app.settings._sections.about";
import { SupportFeedbackForm } from "#support-feedback-form";

/**
 * Crisp's hosted chatbox. Opening this in an in-app browser is what lets us drop
 * `react-native-crisp-chat-sdk` (and the ~15MB `Crisp.framework` it bakes into both app binaries)
 * while still reaching the exact same Crisp workspace and operator inbox.
 *
 * Identity is passed as query params rather than through native setters. The names are Crisp's
 * documented embed params — `token_id` (NOT `tokenId`/`user_token_id`), `user_email`,
 * `user_nickname` — and every value must be URL-encoded, which `URLSearchParams` handles.
 *
 * `token_id` is the one that matters: Crisp keys the conversation on it, so passing the
 * better-auth user id makes a returning user reopen their existing thread instead of starting a
 * fresh anonymous one — the same continuity the native `setTokenId()` call used to provide, and it
 * follows the account across devices and reinstalls. `session_merge=true` tells Crisp to fold any
 * anonymous session the in-app browser already holds into that token's thread rather than leaving
 * two parallel conversations behind. The email/nickname are cosmetic by comparison: they populate
 * the operator's sidebar.
 *
 * A signed-out user gets NO identity params at all, so Crisp opens an anonymous session — the
 * correct behavior for someone contacting support before creating an account, and what the native
 * screen did too.
 */
function getCrispChatUrl(user: { email?: string; id: string; name?: string } | undefined): string {
  const params = new URLSearchParams({ website_id: CRISP_WEBSITE_ID });

  if (user !== undefined) {
    params.set("token_id", user.id);
    params.set("session_merge", "true");

    if (user.email !== undefined && user.email.length > 0) {
      params.set("user_email", user.email);
    }

    if (user.name !== undefined && user.name.length > 0) {
      params.set("user_nickname", user.name);
    }
  }

  return `https://go.crisp.chat/chat/embed/?${params.toString()}`;
}

export function SupportSettingsOptions({ onAboutPress }: { onAboutPress?: () => void } = {}) {
  const { data: session } = getAuthClient().useSession();
  const user = session?.user;
  const [isComposingFeedback, setIsComposingFeedback] = useState(false);

  return (
    <View className="gap-4">
      <View className="gap-3">
        <Text className="text-muted-foreground" variant="p">
          Have a question, hit a bug, or want to suggest something? Start a chat and we will get
          back to you here.
        </Text>
      </View>

      <SettingsRowGroup
        footer="Opens Haniverse's support chat in a secure in-app browser. Your conversation history is kept while you are signed in."
        title="Support Chat"
      >
        <SettingsRow
          accessibilityRole="button"
          icon="MdInfoOutline"
          label="Start a support chat"
          onPress={() => {
            void openBrowserAsync(getCrispChatUrl(user));
          }}
          testID="support-start-chat"
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
      </SettingsRowGroup>
      <SettingsRowGroup footer="Sends your message straight to Leon's email." title="Feedback">
        {isComposingFeedback ? (
          <View className="px-1 py-2">
            <SupportFeedbackForm onCancel={() => setIsComposingFeedback(false)} />
          </View>
        ) : (
          <SettingsRow
            icon="MdBugReport"
            label="Submit Feedback"
            onPress={() => setIsComposingFeedback(true)}
            testID="support-submit-feedback"
          />
        )}
      </SettingsRowGroup>
      <AboutSettingsSection onAboutPress={onAboutPress} />
    </View>
  );
}
